// Tiptap-based rich text editor — replaces TinyMCE across all create/edit flows.
// Usage:  <ArticleEditor initialContent={html} onChange={setHtml} />
// Also exports buildPreviewHTML(rawHTML) for use with DraftPreview.

import React, { useRef } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import ArticleImageView from './ArticleImageView';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

// ── Custom Image extension with NodeView (resize, alt, caption, alignment) ────

const CustomImage = Image.extend({
  draggable: false,
  addAttributes() {
    return {
      ...this.parent?.(),
      width:         { default: null,      parseHTML: el => el.getAttribute('width')                ? Number(el.getAttribute('width'))          : null,  renderHTML: attrs => attrs.width != null ? { width: String(attrs.width) } : {} },
      alignment:     { default: 'center',  parseHTML: el => el.getAttribute('data-alignment')      || 'center', renderHTML: attrs => ({ 'data-alignment': attrs.alignment }) },
      caption:       { default: '',        parseHTML: el => el.getAttribute('data-caption')        || '',       renderHTML: attrs => attrs.caption ? { 'data-caption': attrs.caption } : {} },
      indent:        { default: 0,         parseHTML: el => el.getAttribute('data-indent')         ? Number(el.getAttribute('data-indent'))    : 0,  renderHTML: attrs => attrs.indent ? { 'data-indent': attrs.indent } : {} },
      captionBold:   { default: false,     parseHTML: el => el.getAttribute('data-caption-bold')   === '1',    renderHTML: attrs => attrs.captionBold  ? { 'data-caption-bold': '1' } : {} },
      captionItalic: { default: true,      parseHTML: el => el.getAttribute('data-caption-italic') !== '0',    renderHTML: attrs => ({ 'data-caption-italic': attrs.captionItalic ? '1' : '0' }) },
      captionSize:   { default: 13,        parseHTML: el => el.getAttribute('data-caption-size')   ? Number(el.getAttribute('data-caption-size')) : 13, renderHTML: attrs => ({ 'data-caption-size': String(attrs.captionSize) }) },
      captionColor:  { default: '#555555', parseHTML: el => el.getAttribute('data-caption-color') || '#555555', renderHTML: attrs => ({ 'data-caption-color': attrs.captionColor }) },
      captionAlign:  { default: 'left',    parseHTML: el => el.getAttribute('data-caption-align')  || 'left',  renderHTML: attrs => ({ 'data-caption-align': attrs.captionAlign }) },
      captionMargin: { default: 6,         parseHTML: el => el.getAttribute('data-caption-margin') ? Number(el.getAttribute('data-caption-margin')) : 6, renderHTML: attrs => ({ 'data-caption-margin': String(attrs.captionMargin) }) },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ArticleImageView);
  },
});

// ── Callout / info box node ───────────────────────────────────────────────────

const CALLOUT_VARIANTS = [
  { key: 'info',      label: 'ℹ Info',      border: '#3b82f6', bg: '#eff6ff', text: '#1e3a5f' },
  { key: 'warning',   label: '⚠ Warning',   border: '#f59e0b', bg: '#fffbeb', text: '#78350f' },
  { key: 'tip',       label: '💡 Tip',       border: '#22c55e', bg: '#f0fdf4', text: '#14532d' },
  { key: 'important', label: '❗ Important', border: '#ef4444', bg: '#fef2f2', text: '#7f1d1d' },
];

const CalloutBox = Node.create({
  name: 'calloutBox',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() { return { variant: { default: 'info' } }; },
  parseHTML() { return [{ tag: 'div[data-callout]', getAttrs: el => ({ variant: el.getAttribute('data-callout') }) }]; },
  renderHTML({ node, HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-callout': node.attrs.variant }), 0];
  },
  addCommands() {
    return {
      insertCallout: (attrs = {}) => ({ commands }) =>
        commands.insertContent({ type: 'calloutBox', attrs: { variant: 'info', ...attrs }, content: [{ type: 'paragraph' }] }),
    };
  },
});

// ── Editor CSS ────────────────────────────────────────────────────────────────

const EDITOR_CSS = `
.ae-wrap .tiptap {
  outline: none; min-height: 400px;
  padding: 1.25rem 1.5rem;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 16px; line-height: 1.75; color: #1a1a1a;
}
.ae-wrap .tiptap > * + * { margin-top: 0.6em; }
.ae-wrap .tiptap h1 { font-size: 2em; font-weight: 700; line-height: 1.2; margin: 0.8em 0 0.3em; }
.ae-wrap .tiptap h2 { font-size: 1.5em; font-weight: 700; line-height: 1.3; margin: 0.8em 0 0.3em; }
.ae-wrap .tiptap h3 { font-size: 1.2em; font-weight: 700; margin: 0.8em 0 0.3em; }
.ae-wrap .tiptap p { margin: 0; }
.ae-wrap .tiptap ul, .ae-wrap .tiptap ol { padding-left: 1.5em; }
.ae-wrap .tiptap li > p { margin: 0; }
.ae-wrap .tiptap blockquote {
  border-left: 3px solid #ccc; margin: 0.75em 0;
  padding: 0.4em 1em; background: transparent; color: #555; font-style: italic;
}
.ae-wrap .tiptap code {
  background: #f0f0f0; padding: 0.1em 0.35em;
  border-radius: 3px; font-family: 'Courier New', monospace; font-size: 0.88em;
}
.ae-wrap .tiptap pre { background: #1e1e2e; color: #cdd6f4; padding: 1em 1.25em; border-radius: 7px; overflow-x: auto; }
.ae-wrap .tiptap pre code { background: none; color: inherit; padding: 0; font-size: 0.9em; }
.ae-wrap .tiptap .tableWrapper { overflow-x: auto; max-width: 100%; }
.ae-wrap .tiptap table { border-collapse: collapse; width: 100%; margin: 0.8em 0; table-layout: auto; }
.ae-wrap .tiptap td, .ae-wrap .tiptap th { border: 1px solid #d1d5db; padding: 8px 12px; vertical-align: top; position: relative; min-width: 60px; max-width: 300px; word-break: break-word; }
.ae-wrap .tiptap th { background: #f3f4f6; font-weight: 600; text-align: left; }
.ae-wrap .tiptap .selectedCell::after { z-index: 2; position: absolute; content: ''; left: 0; right: 0; top: 0; bottom: 0; background: rgba(37,99,235,.12); pointer-events: none; }
.ae-wrap .tiptap ul[data-type="taskList"] { padding-left: 0; list-style: none; }
.ae-wrap .tiptap ul[data-type="taskList"] li { display: flex; align-items: flex-start; gap: 0.5em; }
.ae-wrap .tiptap ul[data-type="taskList"] li > label { flex-shrink: 0; margin-top: 3px; }
.ae-wrap .tiptap ul[data-type="taskList"] li > div { flex: 1; }
.ae-wrap .tiptap hr { border: none; border-top: 2px solid #e5e7eb; margin: 1.2em 0; }
.ae-wrap .tiptap a { color: #2563eb; text-decoration: underline; cursor: pointer; }
.ae-wrap .tiptap img { max-width: 100%; height: auto; display: block; margin: 0.8em auto; border-radius: 4px; }
.ae-wrap .tiptap iframe { width: 100%; aspect-ratio: 16/9; margin: 0.8em 0; border-radius: 6px; border: none; }
.ae-wrap .tiptap p.is-editor-empty:first-child::before { color: #adb5bd; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; }
.ae-wrap .tiptap div[data-callout] { border-left: 4px solid; border-radius: 0 6px 6px 0; padding: 0.7em 1.1em; margin: 0.8em 0; }
.ae-wrap .tiptap div[data-callout="info"]      { background: #eff6ff; border-color: #3b82f6; color: #1e3a5f; }
.ae-wrap .tiptap div[data-callout="warning"]   { background: #fffbeb; border-color: #f59e0b; color: #78350f; }
.ae-wrap .tiptap div[data-callout="tip"]       { background: #f0fdf4; border-color: #22c55e; color: #14532d; }
.ae-wrap .tiptap div[data-callout="important"] { background: #fef2f2; border-color: #ef4444; color: #7f1d1d; }
`;

// ── buildPreviewHTML — converts raw editor HTML to display-ready HTML ─────────
// Tiptap's data-* image attrs and NodeView structure are only understood by the
// editor itself. This transforms them into real inline styles so DraftPreview
// (dangerouslySetInnerHTML) shows correct layout.

const CALLOUT_PREVIEW_STYLES = {
  info:      { background: '#eff6ff', borderColor: '#3b82f6', color: '#1e3a5f' },
  warning:   { background: '#fffbeb', borderColor: '#f59e0b', color: '#78350f' },
  tip:       { background: '#f0fdf4', borderColor: '#22c55e', color: '#14532d' },
  important: { background: '#fef2f2', borderColor: '#ef4444', color: '#7f1d1d' },
};

export function imgsMissingAlt(html) {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('img')).filter(img => !img.getAttribute('alt')?.trim()).length;
}

export function buildPreviewHTML(rawHTML) {
  if (!rawHTML) return '';
  const doc = new DOMParser().parseFromString(rawHTML, 'text/html');

  doc.querySelectorAll('img').forEach(img => {
    const alignment     = img.getAttribute('data-alignment')      || 'center';
    const indent        = Number(img.getAttribute('data-indent'))  || 0;
    const caption       = img.getAttribute('data-caption')        || '';
    const captionAlign  = img.getAttribute('data-caption-align')  || 'left';
    const captionBold   = img.getAttribute('data-caption-bold')   === '1';
    const captionItalic = img.getAttribute('data-caption-italic') !== '0';
    const captionSize   = Number(img.getAttribute('data-caption-size'))   || 13;
    const captionColor  = img.getAttribute('data-caption-color')  || '#555555';
    const captionMargin = Number(img.getAttribute('data-caption-margin')) || 6;
    const width         = img.getAttribute('width');

    const textAlign = alignment === 'center' ? 'center' : alignment === 'right' ? 'right' : 'left';

    const outer = doc.createElement('div');
    outer.style.cssText = ['text-align:' + textAlign, 'display:block', 'margin:0.8em 0', indent ? 'padding-left:' + indent + 'px' : ''].filter(Boolean).join(';');

    const inner = doc.createElement('div');
    inner.style.cssText = 'display:inline-block;max-width:100%;vertical-align:top;';

    img.style.display = 'block';
    img.style.width = width ? width + 'px' : '100%';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.borderRadius = '4px';

    ['data-alignment','data-indent','data-caption','data-caption-align',
     'data-caption-bold','data-caption-italic','data-caption-size',
     'data-caption-color','data-caption-margin'].forEach(a => img.removeAttribute(a));

    img.parentNode.insertBefore(outer, img);
    outer.appendChild(inner);
    inner.appendChild(img);

    if (caption) {
      const cap = doc.createElement('div');
      cap.textContent = caption;
      cap.style.cssText = [
        'text-align:' + captionAlign,
        'font-style:' + (captionItalic ? 'italic' : 'normal'),
        'font-weight:' + (captionBold ? '700' : '400'),
        'font-size:' + captionSize + 'px',
        'color:' + captionColor,
        'margin-top:' + captionMargin + 'px',
        'display:block',
      ].join(';');
      inner.appendChild(cap);
    }
  });

  doc.querySelectorAll('table').forEach(table => {
    table.style.cssText = 'border-collapse:collapse;width:100%;margin:0;font-size:0.9rem;line-height:1.4;';
    table.querySelectorAll('td, th').forEach(cell => {
      cell.style.cssText = 'border:1px solid #d1d5db;padding:7px 11px;vertical-align:top;word-break:break-word;';
    });
    table.querySelectorAll('th').forEach(th => {
      th.style.backgroundColor = '#f3f4f6';
      th.style.fontWeight = '600';
      th.style.textAlign = 'left';
    });
    const wrapper = doc.createElement('div');
    wrapper.style.cssText = 'overflow-x:auto;max-width:100%;margin:0.8em 0;';
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });

  doc.querySelectorAll('[data-callout]').forEach(el => {
    const s = CALLOUT_PREVIEW_STYLES[el.getAttribute('data-callout')] || CALLOUT_PREVIEW_STYLES.info;
    el.style.cssText = ['background:' + s.background, 'border-left:4px solid ' + s.borderColor, 'color:' + s.color, 'border-radius:0 6px 6px 0', 'padding:0.7em 1.1em', 'margin:0.8em 0'].join(';');
  });

  return doc.body.innerHTML;
}

// ── Toolbar helpers ───────────────────────────────────────────────────────────

const TBtn = ({ onClick, active, disabled, title, children, style: xStyle = {} }) => (
  <button
    title={title}
    onMouseDown={e => { e.preventDefault(); if (!disabled) onClick(); }}
    style={{
      padding: '3px 7px', fontSize: 13, lineHeight: '1.5',
      background: active ? '#dbeafe' : 'transparent',
      border: active ? '1px solid #93c5fd' : '1px solid transparent',
      borderRadius: 3, cursor: disabled ? 'not-allowed' : 'pointer',
      color: disabled ? '#bbb' : active ? '#1d4ed8' : '#333',
      fontWeight: active ? 700 : 400,
      ...xStyle,
    }}
  >{children}</button>
);

const Sep = () => (
  <span style={{ width: 1, height: 20, background: '#d1d5db', margin: '0 3px', display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }} />
);

const Popup = ({ children }) => (
  <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 200, background: '#fff', border: '1px solid #d1d5db', borderRadius: 7, padding: 12, boxShadow: '0 6px 20px rgba(0,0,0,.15)', minWidth: 270 }}>
    {children}
  </div>
);

const PopupInput = ({ value, onChange, onEnter, placeholder }) => (
  <input value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onEnter()}
    placeholder={placeholder}
    style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
);

const PopupActions = ({ onSave, onCancel, saveLabel = 'Apply', saveColor = '#2563eb' }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    <button onClick={onSave} style={{ flex: 1, padding: '5px 0', background: saveColor, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{saveLabel}</button>
    <button onClick={onCancel} style={{ padding: '5px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
  </div>
);

const FONTS = [
  { label: 'Default',         value: '' },
  { label: 'Serif',           value: 'Georgia, serif' },
  { label: 'Sans-serif',      value: 'Arial, sans-serif' },
  { label: 'Monospace',       value: '"Courier New", monospace' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
];

const selectSt = { fontSize: 12, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer' };

const TABLE_ITEMS = [
  { label: 'Insert 3×3 table',    key: 'insert',   fn: e => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { label: 'Add column before',   key: 'col-bef',  fn: e => e.chain().focus().addColumnBefore().run() },
  { label: 'Add column after',    key: 'col-aft',  fn: e => e.chain().focus().addColumnAfter().run() },
  { label: 'Delete column',       key: 'del-col',  fn: e => e.chain().focus().deleteColumn().run() },
  { label: 'Add row before',      key: 'row-bef',  fn: e => e.chain().focus().addRowBefore().run() },
  { label: 'Add row after',       key: 'row-aft',  fn: e => e.chain().focus().addRowAfter().run() },
  { label: 'Delete row',          key: 'del-row',  fn: e => e.chain().focus().deleteRow().run() },
  { label: 'Toggle header row',   key: 'hdr',      fn: e => e.chain().focus().toggleHeaderRow().run() },
  { label: 'Merge / split cells', key: 'merge',    fn: e => e.chain().focus().mergeOrSplit().run() },
  { label: 'Delete table',        key: 'del-tbl',  fn: e => e.chain().focus().deleteTable().run() },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function ArticleEditor({ initialContent, onChange, placeholder }) {
  const { accessToken: token } = useAuth();
  const [pop, setPop] = React.useState(null);
  const [linkVal,    setLinkVal]    = React.useState('');
  const [imageVal,   setImageVal]   = React.useState('');
  const [imageAlt,   setImageAlt]   = React.useState('');
  const [imageTitle, setImageTitle] = React.useState('');
  const [imageTab,   setImageTab]   = React.useState('url');
  const [youtubeVal, setYoutubeVal] = React.useState('');
  const [showHtml,   setShowHtml]   = React.useState(false);
  const fileRef = useRef();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      CustomImage.configure({ allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow, TableHeader, TableCell,
      TextStyle, Color, FontFamily,
      Subscript, Superscript,
      TaskList, TaskItem.configure({ nested: true }),
      CharacterCount,
      Placeholder.configure({ placeholder: placeholder || 'Start writing your article here…' }),
      Youtube.configure({ controls: true, nocookie: true }),
      CalloutBox,
    ],
    content: initialContent || '',
    onUpdate: ({ editor: ed }) => {
      if (onChange) onChange(ed.getHTML());
    },
    editorProps: {
      transformPastedHTML(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('table, td, th, tr, col, colgroup').forEach(el => {
          el.removeAttribute('width'); el.removeAttribute('height');
          el.removeAttribute('cellpadding'); el.removeAttribute('cellspacing'); el.removeAttribute('bgcolor');
          if (el.style) {
            el.style.removeProperty('width'); el.style.removeProperty('min-width');
            el.style.removeProperty('max-width'); el.style.removeProperty('height');
          }
        });
        return doc.body.innerHTML;
      },
    },
  });

  if (!editor) return null;

  const closePop = () => setPop(null);
  const openPop = (name) => {
    if (name === 'link') setLinkVal(editor.getAttributes('link').href || '');
    setPop(p => p === name ? null : name);
  };

  const applyLink = () => {
    const url = linkVal.trim();
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().setLink({ href: url.startsWith('http') ? url : 'https://' + url, target: '_blank' }).run();
    closePop(); setLinkVal('');
  };

  const applyImageUrl = () => {
    const src = imageVal.trim();
    if (src) editor.chain().focus().setImage({ src, alt: imageAlt.trim(), title: imageTitle.trim() }).run();
    closePop(); setImageVal(''); setImageAlt(''); setImageTitle('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post('/api/upload/image', fd, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
      });
      editor.chain().focus().setImage({ src: res.data.url, alt: imageAlt.trim(), title: imageTitle.trim() }).run();
    } catch {
      alert('Upload failed.');
    }
    closePop(); setImageAlt(''); setImageTitle('');
  };

  const applyYoutube = () => {
    const src = youtubeVal.trim();
    if (src) editor.chain().focus().setYoutubeVideo({ src }).run();
    closePop(); setYoutubeVal('');
  };

  const chars = editor.storage.characterCount.characters();
  const words = editor.storage.characterCount.words();

  let imagesWithoutAlt = 0;
  editor.state.doc.descendants(node => {
    if (node.type.name === 'image' && !node.attrs.alt?.trim()) imagesWithoutAlt++;
  });

  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: 7, overflow: 'visible', background: '#fff' }}>
      <style>{EDITOR_CSS}</style>

      {/* ── Toolbar ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '5px 10px', display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center', borderRadius: '7px 7px 0 0' }}>

        <TBtn title="Undo (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>↩</TBtn>
        <TBtn title="Redo (Ctrl+Y)" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>↪</TBtn>
        <Sep />

        <select title="Paragraph style"
          value={editor.isActive('heading', { level: 1 }) ? 'h1' : editor.isActive('heading', { level: 2 }) ? 'h2' : editor.isActive('heading', { level: 3 }) ? 'h3' : 'p'}
          onChange={e => { const v = e.target.value; v === 'p' ? editor.chain().focus().setParagraph().run() : editor.chain().focus().toggleHeading({ level: Number(v[1]) }).run(); }}
          style={selectSt}>
          <option value="p">Paragraph</option>
          <option value="h1">H1 — Title</option>
          <option value="h2">H2 — Section</option>
          <option value="h3">H3 — Sub</option>
        </select>

        <select title="Font family" onChange={e => { const v = e.target.value; v ? editor.chain().focus().setFontFamily(v).run() : editor.chain().focus().unsetFontFamily().run(); }} style={selectSt}>
          {FONTS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <Sep />

        <TBtn title="Bold (Ctrl+B)"      active={editor.isActive('bold')}      onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></TBtn>
        <TBtn title="Italic (Ctrl+I)"    active={editor.isActive('italic')}    onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></TBtn>
        <TBtn title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></TBtn>
        <TBtn title="Strikethrough"      active={editor.isActive('strike')}    onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></TBtn>
        <TBtn title="Subscript"          active={editor.isActive('subscript')} onClick={() => editor.chain().focus().toggleSubscript().run()} style={{ fontSize: 11 }}>x₂</TBtn>
        <TBtn title="Superscript"        active={editor.isActive('superscript')} onClick={() => editor.chain().focus().toggleSuperscript().run()} style={{ fontSize: 11 }}>x²</TBtn>
        <Sep />

        <label title="Text color" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '2px 5px', borderRadius: 3, border: '1px solid transparent' }}>
          <span style={{ fontSize: 13 }}>A</span>
          <input type="color" defaultValue="#000000" onMouseDown={e => e.stopPropagation()} onChange={e => editor.chain().focus().setColor(e.target.value).run()} style={{ width: 16, height: 16, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 2 }} />
        </label>

        <label title="Highlight color" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '2px 5px', borderRadius: 3, border: '1px solid transparent' }}>
          <span style={{ fontSize: 12, background: '#fef08a', padding: '0 3px', borderRadius: 2 }}>H</span>
          <input type="color" defaultValue="#fef08a" onMouseDown={e => e.stopPropagation()} onChange={e => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} style={{ width: 16, height: 16, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 2 }} />
        </label>
        <Sep />

        <TBtn title="Left"    active={editor.isActive({ textAlign: 'left' })}    onClick={() => editor.chain().focus().setTextAlign('left').run()}>⬅</TBtn>
        <TBtn title="Center"  active={editor.isActive({ textAlign: 'center' })}  onClick={() => editor.chain().focus().setTextAlign('center').run()}>☰</TBtn>
        <TBtn title="Right"   active={editor.isActive({ textAlign: 'right' })}   onClick={() => editor.chain().focus().setTextAlign('right').run()}>➡</TBtn>
        <TBtn title="Justify" active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()}>≡</TBtn>
        <Sep />

        <TBtn title="Bullet list"  active={editor.isActive('bulletList')}  onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</TBtn>
        <TBtn title="Ordered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</TBtn>
        <TBtn title="Task list"    active={editor.isActive('taskList')}    onClick={() => editor.chain().focus().toggleTaskList().run()}>☑ Tasks</TBtn>
        <Sep />

        <TBtn title="Blockquote"  active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>" "</TBtn>
        <TBtn title="Inline code" active={editor.isActive('code')}       onClick={() => editor.chain().focus().toggleCode().run()}>{`</>`}</TBtn>
        <TBtn title="Code block"  active={editor.isActive('codeBlock')}  onClick={() => editor.chain().focus().toggleCodeBlock().run()}>[ ]</TBtn>
        <TBtn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>— HR</TBtn>
        <Sep />

        {/* Link */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Link" active={editor.isActive('link') || pop === 'link'} onClick={() => openPop('link')}>🔗 Link</TBtn>
          {pop === 'link' && (
            <Popup>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 5 }}>URL — leave empty to remove link</div>
              <PopupInput value={linkVal} onChange={setLinkVal} onEnter={applyLink} placeholder="https://example.com" />
              <PopupActions onSave={applyLink} onCancel={closePop} saveLabel="Apply Link" />
            </Popup>
          )}
        </div>

        {/* Image */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Image" active={pop === 'image'} onClick={() => openPop('image')}>🖼 Image</TBtn>
          {pop === 'image' && (
            <Popup>
              <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
                {['url', 'upload'].map(t => (
                  <button key={t} onMouseDown={e => { e.preventDefault(); setImageTab(t); }}
                    style={{ flex: 1, padding: '4px 0', fontSize: 12, borderRadius: 4, cursor: 'pointer', background: imageTab === t ? '#2563eb' : '#f3f4f6', color: imageTab === t ? '#fff' : '#555', border: '1px solid #d1d5db', fontWeight: imageTab === t ? 600 : 400 }}>
                    {t === 'url' ? 'By URL' : 'Upload file'}
                  </button>
                ))}
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#555', marginBottom: 3, fontWeight: 500 }}>
                  Alt text — ADA / screen reader description
                  {!imageAlt.trim() && <span style={{ color: '#ef4444', marginLeft: 4 }}>⚠ required for accessibility</span>}
                </div>
                <input value={imageAlt} onChange={e => setImageAlt(e.target.value)} placeholder="e.g. A photo of city hall" style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, boxSizing: 'border-box', marginBottom: 6 }} />
                <div style={{ fontSize: 11, color: '#555', marginBottom: 3, fontWeight: 500 }}>Title (hover tooltip — optional)</div>
                <input value={imageTitle} onChange={e => setImageTitle(e.target.value)} placeholder="Optional" style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, boxSizing: 'border-box', marginBottom: 8 }} />
              </div>
              {imageTab === 'url' ? (
                <>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 3, fontWeight: 500 }}>Image URL</div>
                  <PopupInput value={imageVal} onChange={setImageVal} onEnter={applyImageUrl} placeholder="https://example.com/photo.jpg" />
                  <PopupActions onSave={applyImageUrl} onCancel={closePop} saveLabel="Insert Image" />
                </>
              ) : (
                <>
                  <p style={{ fontSize: 11, color: '#666', margin: '0 0 8px' }}>Uploads via Cloudinary. Requires login.</p>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { handleImageUpload(e.target.files[0]); e.target.value = ''; }} />
                  <PopupActions onSave={() => fileRef.current?.click()} onCancel={closePop} saveLabel="Choose File" />
                </>
              )}
            </Popup>
          )}
        </div>

        {/* YouTube */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Embed YouTube" active={pop === 'youtube'} onClick={() => openPop('youtube')}>▶ YouTube</TBtn>
          {pop === 'youtube' && (
            <Popup>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 5 }}>Paste a YouTube URL</div>
              <PopupInput value={youtubeVal} onChange={setYoutubeVal} onEnter={applyYoutube} placeholder="https://youtu.be/…" />
              <PopupActions onSave={applyYoutube} onCancel={closePop} saveLabel="Embed" saveColor="#cc0000" />
            </Popup>
          )}
        </div>

        {/* Callout */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Insert or change callout box" active={editor.isActive('calloutBox') || pop === 'callout'} onClick={() => openPop('callout')}>
            📋 Callout{editor.isActive('calloutBox') ? ' ▾' : ' +'}
          </TBtn>
          {pop === 'callout' && (
            <Popup>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 8, fontWeight: 500 }}>
                {editor.isActive('calloutBox') ? 'Change callout style' : 'Insert a callout box'}
              </div>
              {CALLOUT_VARIANTS.map(v => (
                <button key={v.key} onMouseDown={e => {
                  e.preventDefault();
                  editor.isActive('calloutBox')
                    ? editor.chain().focus().updateAttributes('calloutBox', { variant: v.key }).run()
                    : editor.chain().focus().insertCallout({ variant: v.key }).run();
                  closePop();
                }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', marginBottom: 5, background: v.bg, color: v.text, border: `1px solid ${v.border}`, borderLeft: `4px solid ${v.border}`, borderRadius: '0 5px 5px 0', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                  {v.label}
                </button>
              ))}
            </Popup>
          )}
        </div>

        {/* Table */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Table operations" active={pop === 'table'} onClick={() => openPop('table')}>⊞ Table ▾</TBtn>
          {pop === 'table' && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 200, background: '#fff', border: '1px solid #d1d5db', borderRadius: 7, overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,.15)', minWidth: 190 }}>
              {TABLE_ITEMS.map(item => (
                <button key={item.key} onMouseDown={e => { e.preventDefault(); item.fn(editor); closePop(); }}
                  style={{ display: 'block', width: '100%', padding: '7px 14px', fontSize: 12, textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', color: '#333' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >{item.label}</button>
              ))}
            </div>
          )}
        </div>

      </div>
      {/* ── End toolbar ── */}

      {/* Editor content */}
      <div className="ae-wrap" onClick={() => { if (pop) closePop(); }} style={{ minHeight: 400, borderBottom: '1px solid #e5e7eb' }}>
        <EditorContent editor={editor} />
      </div>

      {/* Footer bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 14px', background: '#f9fafb', fontSize: 12, color: '#888', borderRadius: '0 0 7px 7px' }}>
        <span>
          {words.toLocaleString()} words · {chars.toLocaleString()} characters
          {imagesWithoutAlt > 0 && <span style={{ marginLeft: 10, color: '#b45309', fontWeight: 600 }}>⚠ {imagesWithoutAlt} image{imagesWithoutAlt > 1 ? 's' : ''} missing alt text</span>}
        </span>
        <button onClick={() => setShowHtml(v => !v)} style={{ fontSize: 11, padding: '2px 10px', background: showHtml ? '#dbeafe' : '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: showHtml ? '#1d4ed8' : '#555' }}>
          {showHtml ? 'Hide' : 'View'} HTML
        </button>
      </div>

      {showHtml && (
        <div style={{ background: '#1e1e2e', padding: '1rem 1.25rem', maxHeight: 240, overflow: 'auto', borderRadius: '0 0 7px 7px' }}>
          <pre style={{ margin: 0, fontSize: 12, color: '#cdd6f4', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: '"Courier New", monospace', lineHeight: 1.55 }}>
            {editor.getHTML()}
          </pre>
        </div>
      )}
    </div>
  );
}
